import { type RxCollection } from 'rxdb';

export const createOfflineUUID = () => crypto.randomUUID();

const getNow = () => Date.now();
const getISODate = () => new Date().toISOString();

class OfflineSyncError extends Error {
  constructor(
    public readonly operation: string,
    public readonly collectionName: string,
    public readonly docs: unknown,
    public override readonly cause?: Error
  ) {
    const message = `RxDB Sync: ${operation} failed (${collectionName})`;
    super(message, { cause });
    this.name = 'OfflineSyncError';
  }
}

export const bulkUpsertSynced = async <
  T extends { syncedAt: number; offlineSyncErrors?: unknown },
>(
  collection: RxCollection<T>,
  docs: Omit<T, 'syncedAt'>[],
  syncedAt = getNow()
) => {
  if (!docs.length) {
    return [];
  }

  const key = collection.schema.primaryPath as keyof Omit<T, 'syncedAt'>;
  const existing = await collection
    .findByIds(docs.map((d) => String(d[key])))
    .exec();
  const blockedIds = new Set(
    Array.from(existing.values())
      .map((d) => d.toMutableJSON())
      .filter((d) => d.syncedAt === 0 || !!d.offlineSyncErrors)
      .map((d) => String(d[key as keyof T]))
  );
  const safeDocs = docs.filter((d) => !blockedIds.has(String(d[key])));
  if (!safeDocs.length) {
    return [];
  }

  const res = await collection.bulkUpsert(
    safeDocs.map((d) => ({ ...d, syncedAt }) as Partial<T>)
  );
  if (res.error.length) {
    throw new OfflineSyncError(
      'bulk upsert',
      collection.name,
      res.error.map(({ documentId, status }) => ({ documentId, status }))
    );
  }
  return res.success.map((d) => d.toMutableJSON());
};

export const bulkInsertSynced = async <T extends { syncedAt: number }>(
  collection: RxCollection<T>,
  docs: Omit<T, 'syncedAt'>[],
  syncedAt = getNow()
) => {
  const res = await collection.bulkInsert(
    docs.map((d) => ({ ...d, syncedAt }) as T)
  );
  if (res.error.length) {
    throw new OfflineSyncError(
      'bulk insert',
      collection.name,
      res.error.map(({ documentId, status }) => ({ documentId, status }))
    );
  }
  return res.success.map((d) => d.toMutableJSON());
};

export const insertSynced = async <T extends { syncedAt: number }>(
  collection: RxCollection<T>,
  item: Omit<T, 'syncedAt'>,
  syncedAt = getNow()
) => {
  try {
    const doc = await collection.insert({ ...item, syncedAt } as T);
    return doc.toMutableJSON();
  } catch (error) {
    throw new OfflineSyncError(
      'insert',
      collection.name,
      {
        id: item[collection.schema.primaryPath as keyof typeof item],
        fields: Object.keys(item),
      },
      error instanceof Error ? error : undefined
    );
  }
};

export const upsertSynced = async <T extends { syncedAt: number }>(
  collection: RxCollection<T>,
  item: Omit<T, 'syncedAt'>,
  syncedAt = getNow()
) => {
  try {
    const doc = await collection.upsert({ ...item, syncedAt } as T);
    return doc.toMutableJSON();
  } catch (error) {
    throw new OfflineSyncError(
      'upsert',
      collection.name,
      {
        id: item[collection.schema.primaryPath as keyof typeof item],
        fields: Object.keys(item),
      },
      error instanceof Error ? error : undefined
    );
  }
};

export const patchSynced = async <T extends { syncedAt: number }>(
  collection: RxCollection<T>,
  id: string,
  item: Partial<Omit<T, 'syncedAt'>>,
  syncedAt = getNow()
) => {
  const existing = await collection.findOne(id).exec();
  if (!existing) {
    throw new OfflineSyncError('Find for patch', collection.name, { id });
  }

  try {
    const updated = await existing.incrementalPatch({
      ...item,
      syncedAt,
      updatedAt: getISODate(),
    } as unknown as Partial<T>);
    return updated.toMutableJSON();
  } catch (error) {
    throw new OfflineSyncError(
      'patch',
      collection.name,
      { id, fields: Object.keys(item) },
      error instanceof Error ? error : undefined
    );
  }
};
